/**
 * Adding the thing you have just discovered is missing, without losing the form you are in.
 *
 * **The tenancy form is where this bites.** It asks for a unit and a tenant, and half the time one
 * of them does not exist yet — a new tenant signing for a flat that was added this morning. Sending
 * somebody to another screen to make it means losing the dates, the rent and the charges they had
 * already typed, so the honest answer is to make it here.
 *
 * Both of these post to the same endpoints the full forms use, so nothing is created a second way:
 * the difference is only which fields are asked for. A unit made here is vacant and unlisted, and a
 * tenant is a name and a way of reaching them — everything else is on the full form, where somebody
 * can go afterwards.
 */

import { useState, type FormEvent } from "react";
import { RENT_CYCLES } from "@rentorng/core";
import { api } from "@/lib/api";
import { formValues, nullIfBlank, useApiMutation } from "@/lib/mutations";
import { useSession } from "@/lib/session";
import { titleCase } from "@/lib/format";
import { resolveProperty, searchProperties } from "@/lib/pickers";
import { SearchSelect } from "@/components/search-select";
import { MoneyInput } from "@/components/money-input";
import { PhoneInput } from "@/components/phone-input";
import { Alert, Button, Field, Input, Modal, Select } from "@/components/ui";

/** The button that opens one of these. Small, quiet, and next to the picker it fills in. */
export function QuickAddButton({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick} className="link text-xs font-medium">
      {children}
    </button>
  );
}

export function QuickAddUnit({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  /** The new unit's id, so the picker that opened this can select it. */
  onCreated: (id: string) => void;
}) {
  const { vocab } = useSession();
  const [propertyId, setPropertyId] = useState("");
  const [cycle, setCycle] = useState("annual");

  const create = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.post<{ id: string }>("/units", input),
    invalidates: [["units"], ["vacant-units"], ["dashboard"]],
    onSuccess: (made: { id?: string } | undefined) => {
      if (made?.id) onCreated(made.id);
      onClose();
    },
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = formValues(event.currentTarget);
    create.mutate({
      propertyId,
      label: values.label,
      status: "vacant",
      rentAmount: nullIfBlank(values.rentAmount),
      rentCycle: cycle,
      rentCycleEvery: 1,
      offers: ["rent"],
      /* Not on the public site until somebody has looked at it — this form asks for four fields,
         and a listing made from four fields is not a listing. */
      listed: false,
    });
  };

  return (
    <Modal open={open} onClose={onClose} title={`Add a ${vocab.unit}`}>
      <form onSubmit={submit} className="space-y-4">
        {create.errorMessage ? <Alert tone="danger">{create.errorMessage}</Alert> : null}

        <Field label={titleCase(vocab.property)} htmlFor="quickProperty" required>
          <SearchSelect
            id="quickProperty"
            value={propertyId}
            onChange={setPropertyId}
            search={searchProperties}
            resolve={resolveProperty}
            placeholder={`Search for a ${vocab.property}…`}
            emptyMessage={`No ${vocab.property} matched.`}
          />
        </Field>

        <Field label="Label" htmlFor="quickLabel" required hint="Whatever you call it on the keys.">
          <Input id="quickLabel" name="label" required placeholder="Flat 3B" />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={`Asking ${vocab.rent}`} htmlFor="quickRent">
            <MoneyInput id="quickRent" name="rentAmount" placeholder="3,500,000" />
          </Field>
          <Field label="Paid" htmlFor="quickCycle">
            <Select id="quickCycle" value={cycle} onChange={(event) => setCycle(event.target.value)}>
              {RENT_CYCLES.map((entry) => (
                <option key={entry.value} value={entry.value}>
                  {entry.label}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <p className="text-xs text-ink-500">
          It is created vacant and off the public site. Everything else — photographs, amenities,
          the {vocab.landlord} — is on the full form.
        </p>

        <div className="flex gap-2 pt-1">
          <Button type="submit" disabled={create.isPending || !propertyId}>
            {create.isPending ? "Adding…" : `Add ${vocab.unit}`}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function QuickAddTenant({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const { vocab } = useSession();

  const create = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.post<{ id: string }>("/parties", input),
    invalidates: [["parties"], ["clients"]],
    onSuccess: (made: { id?: string } | undefined) => {
      if (made?.id) onCreated(made.id);
      onClose();
    },
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = formValues(event.currentTarget);
    create.mutate({
      name: values.name,
      roles: ["tenant"],
      phones: values.phone ? [{ number: values.phone, whatsapp: true }] : undefined,
      email: nullIfBlank(values.email),
    });
  };

  return (
    <Modal open={open} onClose={onClose} title={`Add a ${vocab.tenant}`}>
      <form onSubmit={submit} className="space-y-4">
        {create.errorMessage ? <Alert tone="danger">{create.errorMessage}</Alert> : null}

        <Field label="Full name" htmlFor="quickName" required>
          <Input id="quickName" name="name" required placeholder="Tunde Bakare" />
        </Field>

        <Field label="Phone" htmlFor="quickPhone" hint="Assumed to be on WhatsApp, which is where this market talks.">
          <PhoneInput id="quickPhone" name="phone" />
        </Field>

        <Field label="Email (optional)" htmlFor="quickEmail">
          <Input id="quickEmail" name="email" type="email" placeholder="them@example.com" />
        </Field>

        <div className="flex gap-2 pt-1">
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? "Adding…" : `Add ${vocab.tenant}`}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
}
